import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { DashboardTile } from './DashboardTile';
import { tokens } from '../../../design';

/**
 * Single queue-count tile — mirrors the `.t-failed` tile in
 * `design/mockups/admin/dashboard.html`: an accent icon box + a big mono
 * number + a caption. Fed by one `AdminQueueCountsDto` field.
 *
 * Two tones:
 *   - `danger`  — failed payouts / suspended cards (red icon chip)
 *   - `neutral` — moderation queues (brand-subtle icon chip)
 *
 * The whole tile is a router `<Link>` to the matching queue page so ops
 * can jump straight into the work.
 */
export type QueueTileTone = 'danger' | 'neutral';

const TileLink = styled(RouterLink)({
  textDecoration: 'none',
  color: 'inherit',
  display: 'block',
  minWidth: 0,
  '&:hover .queue-tile-card': {
    boxShadow: `0 1px 0 ${tokens.border.strong}, 0 10px 20px -12px rgba(12, 20, 15, 0.16)`,
  },
});

const IconChip = styled('div')<{ tone: QueueTileTone }>(({ tone }) => ({
  width: 48,
  height: 48,
  borderRadius: 14,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  background: tone === 'danger' ? tokens.state.dangerBg : tokens.brand.subtle,
  color: tone === 'danger' ? tokens.state.danger : tokens.brand.primary,
  '& svg': {
    width: 22,
    height: 22,
    stroke: 'currentColor',
    fill: 'none',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  },
}));

const Big = styled('div')({
  fontFamily: tokens.font.mono,
  fontSize: 28,
  fontWeight: 700,
  lineHeight: 1,
  color: tokens.text.primary,
});

export interface QueueCountTileProps {
  label: string;
  count: number | undefined;
  icon: ReactNode;
  to: string;
  tone?: QueueTileTone | undefined;
  /**
   * Optional plain-language description of the predicate behind the
   * number, surfaced on hover. A count whose meaning is not obvious from
   * its label is a count people learn to ignore.
   */
  hint?: string | undefined;
  isLoading: boolean;
  isError: boolean;
  gridColumn?: string;
}

export const QueueCountTile = ({
  label,
  count,
  icon,
  to,
  tone = 'neutral',
  hint,
  isLoading,
  isError,
  gridColumn = 'span 3',
}: QueueCountTileProps): ReactElement => {
  const { t } = useTranslation();

  const body = ((): ReactNode => {
    if (isLoading) return <CircularProgress size={20} />;
    if (isError || typeof count !== 'number') {
      return (
        <Typography variant="body2" sx={{ color: tokens.state.danger }}>
          {t('dashboard.loadError')}
        </Typography>
      );
    }
    return <Big>{count}</Big>;
  })();

  return (
    <TileLink to={to} aria-label={label} sx={{ gridColumn }} {...(hint ? { title: hint } : {})}>
      <DashboardTile className="queue-tile-card" sx={{ height: '100%' }}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            color: tokens.text.tertiary,
            mb: 1.5,
          }}
        >
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 'auto' }}>
          <IconChip tone={tone}>{icon}</IconChip>
          <Box sx={{ minWidth: 0 }}>{body}</Box>
        </Box>
      </DashboardTile>
    </TileLink>
  );
};
